import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, useTheme } from "@/App";
import api from "@/lib/api";
import { Building2, Upload, Check, Loader2, ImageIcon, Clock, Plus, Trash2, Palette } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
  const { user } = useAuth();
  const { accentColor, setAccentColor } = useTheme();
  const fileInputRef = useRef(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [editInfo, setEditInfo] = useState({});
  const [saving, setSaving] = useState(false);
  const [logoKey, setLogoKey] = useState(0);
  const [shiftsConfig, setShiftsConfig] = useState([]);
  const [shiftsLoading, setShiftsLoading] = useState(true);
  const [shiftsSaving, setShiftsSaving] = useState(false);

  const tenantId = user?.tenant_id;
  const canEdit = user?.role === "tenant_admin" || user?.role === "super_admin";

  const loadLogo = React.useCallback(() => {
    if (!tenantId) return;
    setLogoUrl(`${API_URL}/api/tenants/${tenantId}/logo?t=${Date.now()}`);
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}`).then(r => {
      setTenantInfo(r.data);
      setEditInfo({ name: r.data.name, address: r.data.address || "", phone: r.data.phone || "", license_number: r.data.license_number || "" });
    }).catch(() => {});
    loadLogo();
    api.get("/shifts-config").then(r => {
      setShiftsConfig(r.data);
      setShiftsLoading(false);
    }).catch(() => setShiftsLoading(false));
  }, [tenantId, loadLogo]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Solo se permiten archivos de imagen"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("El archivo no puede superar 2MB"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post(`/tenants/${tenantId}/logo`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo actualizado correctamente");
      setLogoKey(prev => prev + 1);
      loadLogo();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al subir el logo"); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const handleSaveInfo = async () => {
    setSaving(true);
    try {
      await api.put(`/tenants/${tenantId}`, editInfo);
      setTenantInfo(prev => ({ ...prev, ...editInfo }));
      toast.success("Datos actualizados correctamente");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const makeShiftKey = (label) => label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");

  const addShift = () => {
    setShiftsConfig([...shiftsConfig, { key: `turno_${Date.now()}`, label: "", start: "08:00", end: "16:00" }]);
  };

  const updateShift = (i, field, value) => {
    const updated = [...shiftsConfig];
    updated[i] = { ...updated[i], [field]: value };
    if (field === "label") {
      updated[i].key = makeShiftKey(value);
    }
    setShiftsConfig(updated);
  };

  const removeShift = (i) => {
    if (shiftsConfig.length <= 1) { toast.error("Debe haber al menos un turno"); return; }
    setShiftsConfig(shiftsConfig.filter((_, idx) => idx !== i));
  };

  const handleSaveShifts = async () => {
    const invalid = shiftsConfig.find(s => !s.label.trim() || !s.start || !s.end);
    if (invalid) { toast.error("Completá nombre y horarios de todos los turnos"); return; }
    setShiftsSaving(true);
    try {
      const clean = shiftsConfig.map(s => ({ ...s, key: makeShiftKey(s.label) || s.key }));
      const res = await api.put("/shifts-config", { shifts: clean });
      setShiftsConfig(res.data);
      toast.success("Turnos actualizados correctamente");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar turnos"); }
    finally { setShiftsSaving(false); }
  };

  if (!canEdit) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="settings-no-access">
        <p className="text-slate-400 text-lg">No tenés permisos para acceder a la configuración.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight font-[Manrope]">Configuración</h1>
        <p className="text-sm text-slate-400 mt-0.5">Personalizá tu geriátrico</p>
      </div>

      <Card className="rounded-2xl border-slate-100 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
              <ImageIcon className="w-5 h-5 text-violet-600" strokeWidth={1.8} />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Logo del Geriátrico</h2>
              <p className="text-xs text-slate-400">Se muestra en el sidebar y reportes</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-start gap-5">
            <div className="w-28 h-28 rounded-xl border border-dashed border-slate-200 flex items-center justify-center overflow-hidden bg-slate-50 flex-shrink-0" data-testid="logo-preview">
              <img key={logoKey} src={logoUrl} alt="Logo" className="w-full h-full object-contain"
                onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                onLoad={(e) => { e.target.style.display = "block"; if (e.target.nextSibling) e.target.nextSibling.style.display = "none"; }}
              />
              <div className="flex flex-col items-center justify-center text-slate-300" style={{ display: "flex" }}>
                <Building2 className="w-8 h-8 mb-1" strokeWidth={1.5} />
                <span className="text-[10px] font-medium">Sin logo</span>
              </div>
            </div>
            <div className="flex-1 space-y-3">
              <p className="text-xs text-slate-400">PNG, JPG, SVG. Máximo: 2MB. Recomendado: 512x512px.</p>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} data-testid="logo-file-input" />
              <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold px-5 h-10 text-sm active:scale-[0.98] transition-all"
                data-testid="upload-logo-btn">
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subiendo...</> : <><Upload className="w-4 h-4 mr-2" /> Subir Logo</>}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-slate-100 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-emerald-600" strokeWidth={1.8} />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Datos del Geriátrico</h2>
              <p className="text-xs text-slate-400">Información básica de la residencia</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Nombre</Label>
              <Input value={editInfo.name || ""} onChange={e => setEditInfo(prev => ({ ...prev, name: e.target.value }))} className="rounded-xl h-10 border-slate-200 bg-slate-50 focus:border-violet-500 text-sm" data-testid="settings-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Teléfono</Label>
              <Input value={editInfo.phone || ""} onChange={e => setEditInfo(prev => ({ ...prev, phone: e.target.value }))} className="rounded-xl h-10 border-slate-200 bg-slate-50 focus:border-violet-500 text-sm" data-testid="settings-phone-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Dirección</Label>
              <Input value={editInfo.address || ""} onChange={e => setEditInfo(prev => ({ ...prev, address: e.target.value }))} className="rounded-xl h-10 border-slate-200 bg-slate-50 focus:border-violet-500 text-sm" data-testid="settings-address-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">N° Habilitación</Label>
              <Input value={editInfo.license_number || ""} onChange={e => setEditInfo(prev => ({ ...prev, license_number: e.target.value }))} className="rounded-xl h-10 border-slate-200 bg-slate-50 focus:border-violet-500 text-sm" data-testid="settings-license-input" />
            </div>
          </div>
          <Separator className="my-5 bg-slate-100" />
          <div className="flex justify-end">
            <Button onClick={handleSaveInfo} disabled={saving}
              className="rounded-xl bg-accent-600 hover:bg-accent-700 text-white font-semibold px-6 h-10 text-sm active:scale-[0.98] transition-all"
              data-testid="save-settings-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</> : <><Check className="w-4 h-4 mr-2" /> Guardar Cambios</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Accent Color */}
      <Card className="rounded-2xl border-slate-100 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center">
              <Palette className="w-5 h-5 text-accent-600" strokeWidth={1.8} />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Color de la Plataforma</h2>
              <p className="text-xs text-slate-400">Personalizá los botones y acentos con el color de tu institución</p>
            </div>
          </div>
          <div className="grid grid-cols-5 gap-3" data-testid="accent-color-picker">
            {[
              { key: "violet", label: "Violeta", color: "#7C3AED", ring: "ring-violet-300" },
              { key: "blue", label: "Azul", color: "#2563EB", ring: "ring-blue-300" },
              { key: "green", label: "Verde", color: "#059669", ring: "ring-emerald-300" },
              { key: "teal", label: "Celeste", color: "#0891B2", ring: "ring-cyan-300" },
              { key: "rose", label: "Rojo", color: "#E11D48", ring: "ring-rose-300" },
            ].map(c => {
              const selected = accentColor === c.key;
              return (
                <button
                  key={c.key}
                  onClick={async () => {
                    setAccentColor(c.key);
                    try { await api.put(`/tenants/${tenantId}`, { accent_color: c.key }); toast.success(`Color actualizado a ${c.label}`); }
                    catch { /* still applies locally */ }
                  }}
                  className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all ${
                    selected ? `border-slate-900 shadow-md ring-4 ${c.ring}` : "border-slate-100 hover:border-slate-300"
                  }`}
                  data-testid={`accent-${c.key}`}
                >
                  <div
                    className="w-10 h-10 rounded-full shadow-inner flex items-center justify-center"
                    style={{ backgroundColor: c.color }}
                  >
                    {selected && <Check className="w-5 h-5 text-white" />}
                  </div>
                  <span className={`text-xs font-semibold ${selected ? "text-slate-900" : "text-slate-400"}`}>{c.label}</span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Shifts Configuration */}
      <Card className="rounded-2xl border-slate-100 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" strokeWidth={1.8} />
            </div>
            <div className="flex-1">
              <h2 className="text-sm font-semibold text-slate-900">Turnos del Geriátrico</h2>
              <p className="text-xs text-slate-400">Configurá los horarios de cada turno de trabajo</p>
            </div>
            <Button
              onClick={addShift}
              variant="outline"
              className="rounded-xl h-9 px-3.5 text-xs font-semibold border-slate-200 text-slate-600 hover:bg-slate-50"
              data-testid="add-shift-config-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Agregar turno
            </Button>
          </div>

          {shiftsLoading ? (
            <p className="text-slate-400 text-center py-6">Cargando turnos...</p>
          ) : (
            <div className="space-y-3">
              {shiftsConfig.map((shift, i) => (
                <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-xl p-4" data-testid={`shift-config-${i}`}>
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Nombre del turno</Label>
                      <Input
                        value={shift.label}
                        onChange={e => updateShift(i, "label", e.target.value)}
                        placeholder="Ej: Mañana"
                        className="rounded-xl h-9 bg-white border-slate-200 text-sm font-medium"
                        data-testid={`shift-label-${i}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Hora inicio</Label>
                      <Input
                        type="time"
                        value={shift.start}
                        onChange={e => updateShift(i, "start", e.target.value)}
                        className="rounded-xl h-9 bg-white border-slate-200 text-sm"
                        data-testid={`shift-start-${i}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Hora fin</Label>
                      <Input
                        type="time"
                        value={shift.end}
                        onChange={e => updateShift(i, "end", e.target.value)}
                        className="rounded-xl h-9 bg-white border-slate-200 text-sm"
                        data-testid={`shift-end-${i}`}
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => removeShift(i)}
                    className="text-slate-300 hover:text-rose-500 transition-colors flex-shrink-0 p-1"
                    data-testid={`remove-shift-${i}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <Separator className="my-5 bg-slate-100" />
          <div className="flex justify-end">
            <Button onClick={handleSaveShifts} disabled={shiftsSaving}
              className="rounded-xl bg-accent-600 hover:bg-accent-700 text-white font-semibold px-6 h-10 text-sm active:scale-[0.98] transition-all"
              data-testid="save-shifts-btn">
              {shiftsSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</> : <><Check className="w-4 h-4 mr-2" /> Guardar Turnos</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
